import Editor from '@draft-js-plugins/editor';
import createMentionPlugin, { defaultSuggestionsFilter } from '@draft-js-plugins/mention';
import classNames from 'classnames';
import { EditorState } from 'draft-js';
import { stateFromHTML } from 'draft-js-import-html';
import useSelect from 'hooks/useSelect';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectPersonalId } from 'store/selectors/auth.selector';
import EntryComponent from './components/EntryComponent';
import SuggestionsComponent from './components/SuggestionsComponent';
import './styles.scss';
import { toDraftWithMentions, toHtmlWithMentions } from './utils';

const MentionTextareaField = ({ input, meta, className, label, required, ...rest }) => {
  const userSuggestions = useSelector(state =>
    state.global.usersToAssign.map(({ id, name }) => ({
      name: `@${name}`,
      id,
    })),
  );
  const editorRef = useRef(null);
  const [suggestions, setSuggestions] = useState(userSuggestions);
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [open, setOpen] = useState(false);
  const curentUserId = useSelect(selectPersonalId);

  const { MentionSuggestions, plugins } = useMemo(() => {
    const mentionPlugin = createMentionPlugin({
      theme: {
        mention: 'mention',
        mentionSuggestions: 'mentionSuggestions',
        mentionPrefix: '@',
      },
    });
    const { MentionSuggestions } = mentionPlugin;
    const plugins = [mentionPlugin];
    return { plugins, MentionSuggestions };
  }, []);

  const error = useMemo(() => (meta ? meta.error || meta.submitError : null), [meta]);

  const hasError = useMemo(() => {
    if (meta) {
      const { invalid, touched: metaTouched, error: metaError, submitError, dirtySinceLastSubmit } = meta;
      if (submitError && !dirtySinceLastSubmit) {
        return submitError;
      }
      return metaTouched && invalid && metaError;
    }
    return false;
  }, [meta]);

  const onSearchChange = useCallback(
    ({ value }) => {
      setSuggestions(defaultSuggestionsFilter(value, userSuggestions));
    },
    [userSuggestions],
  );

  const onOpenChange = useCallback(open => {
    setOpen(open);
  }, []);

  const extractMentionsFromEditorState = useCallback(editorState => {
    const contentState = editorState?.getCurrentContent();
    const mentions = new Set();

    // Iterate through all blocks and extract mentions
    contentState.getBlockMap().forEach(block => {
      block.findEntityRanges(
        character => {
          const entityKey = character.getEntity();
          if (entityKey !== null) {
            const entity = contentState.getEntity(entityKey);
            return entity.getType() === 'mention'; // Only keep mention entities
          }
          return false;
        },
        start => {
          const entityKey = block.getEntityAt(start);
          if (entityKey !== null) {
            const entity = contentState.getEntity(entityKey);
            const mentionData = entity.getData();
            if (mentionData && mentionData.mention) {
              mentions.add(mentionData.mention.id);
            }
          }
        },
      );
    });

    return Array.from(mentions); // Convert Set to Array
  }, []);

  const handleEditorChange = useCallback(
    newEditorState => {
      const mentions = extractMentionsFromEditorState(newEditorState);
      const mentionsWithoutDuplicats = [...new Set(mentions)];
      const editorHtmlText = toHtmlWithMentions(newEditorState);
      const editorPlainText = newEditorState?.getCurrentContent().getPlainText();
      const uniqueMentionsWithoutCurrentUser = mentionsWithoutDuplicats.filter(
        mention => editorHtmlText.includes(mention) && mention !== curentUserId,
      );

      input.onChange({
        editorHtmlText,
        mentionedUsersIds: uniqueMentionsWithoutCurrentUser,
        body: editorPlainText,
      });

      setEditorState(newEditorState);
    },
    [curentUserId],
  );

  useEffect(() => {
    setEditorState(old => {
      if (toHtmlWithMentions(old) !== input?.value?.editorHtmlText) {
        // Convert HTML to Draft.js content state
        const contentStateWithoutMentions = stateFromHTML(input?.value?.editorHtmlText || '');

        // Apply mention entities
        const contentStateWithMentions = toDraftWithMentions(contentStateWithoutMentions);

        // Create editor state
        const newEditorState = EditorState.createWithContent(contentStateWithMentions);

        return newEditorState;
      } else {
        return old;
      }
    });
  }, [input?.value]);

  const classes = classNames('ickyc-mention-textarea-field', className, {
    'ickyc-mention-textarea-field--required': required,
  });

  return (
    <div className={classes}>
      {label && <label className="ickyc-mention-textarea-field__label">{label}</label>}
      <div className="ickyc-mention-textarea-field__editor">
        <Editor
          editorKey={'editor'}
          editorState={editorState}
          onChange={handleEditorChange}
          plugins={plugins}
          ref={editorRef}
          {...rest}
        />
        <MentionSuggestions
          className="ickyc-mention-textarea-field__suggestions"
          open={open}
          onOpenChange={onOpenChange}
          suggestions={suggestions}
          onSearchChange={onSearchChange}
          entryComponent={EntryComponent}
          popoverContainer={SuggestionsComponent}
        />
      </div>
      {hasError && <div className="ickyc-mention-textarea-field__error">{error}</div>}
    </div>
  );
};

MentionTextareaField.propTypes = {
  input: PropTypes.object,
  meta: PropTypes.shape({
    invalid: PropTypes.bool,
    touched: PropTypes.bool,
    error: PropTypes.string,
    submitError: PropTypes.string,
    dirtySinceLastSubmit: PropTypes.bool,
  }),
  className: PropTypes.string,
};
MentionTextareaField.defaultProps = {
  className: undefined,
  input: null,
  meta: { invalid: false, touched: false, error: '', submitError: '', dirtySinceLastSubmit: false },
};
export default MentionTextareaField;
